import { useCallback, useEffect, useState } from 'react';
import { reportsGateway } from '../index';
import type { CashRequirementResponse, PayrollRegisterResponse, PayrollRunSummary } from '../types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

export function PayrollRegisterPage() {
  const [runs, setRuns] = useState<PayrollRunSummary[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [register, setRegister] = useState<PayrollRegisterResponse | null>(null);
  const [cashRequirement, setCashRequirement] = useState<CashRequirementResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRuns(await reportsGateway.listApprovedPayrollRuns());
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load approved payroll runs.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReports = useCallback(async (payrollRunId: string) => {
    setError(null);
    try {
      const [registerResult, cashRequirementResult] = await Promise.all([
        reportsGateway.getPayrollRegister(payrollRunId),
        reportsGateway.getCashRequirement(payrollRunId),
      ]);
      setRegister(registerResult);
      setCashRequirement(cashRequirementResult);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load the payroll register.'));
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    if (selectedRunId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      void loadReports(selectedRunId);
    }
  }, [selectedRunId, loadReports]);

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>Payroll Register &amp; Cash Requirement</h2>
      </div>

      {error && <p className="error">{error}</p>}
      {loading && <p className="muted">Loading approved payroll runs…</p>}
      {!loading && runs.length === 0 && <p className="muted">No Approved payroll runs yet.</p>}

      {!loading && runs.length > 0 && (
        <table>
          <thead>
            <tr><th>Period</th><th>Check Date</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id}>
                <td>{r.periodStartDate} – {r.periodEndDate}</td>
                <td>{r.checkDate}</td>
                <td>{r.status}</td>
                <td>
                  <button type="button" className="link" onClick={() => setSelectedRunId(r.id)}>
                    {selectedRunId === r.id ? 'Selected' : 'Select'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {cashRequirement && (
        <p>
          Cash requirement: <strong>{cashRequirement.totalCashRequired.toFixed(2)}</strong>{' '}
          (gross {cashRequirement.totalGrossEarnings.toFixed(2)} + employer taxes {cashRequirement.totalEmployerTaxes.toFixed(2)})
        </p>
      )}

      {register && (
        <>
          <table>
            <thead>
              <tr><th>Employee</th><th>Gross</th><th>Employee Taxes</th><th>Employer Taxes</th><th>Pre-Tax</th><th>Post-Tax</th><th>Garnishments</th><th>Net Pay</th></tr>
            </thead>
            <tbody>
              {register.lines.map((line) => (
                <tr key={line.employeeId}>
                  <td>{line.employeeNumber} — {line.employeeName}</td>
                  <td>{line.grossEarnings.toFixed(2)}</td>
                  <td>{line.employeeTaxes.toFixed(2)}</td>
                  <td>{line.employerTaxes.toFixed(2)}</td>
                  <td>{line.preTaxDeductionsTotal.toFixed(2)}</td>
                  <td>{line.postTaxDeductionsTotal.toFixed(2)}</td>
                  <td>{line.garnishmentsTotal.toFixed(2)}</td>
                  <td>{line.netPay.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Totals</strong></td>
                <td>{register.totals.grossEarnings.toFixed(2)}</td>
                <td>{register.totals.employeeTaxes.toFixed(2)}</td>
                <td>{register.totals.employerTaxes.toFixed(2)}</td>
                <td>{register.totals.preTaxDeductionsTotal.toFixed(2)}</td>
                <td>{register.totals.postTaxDeductionsTotal.toFixed(2)}</td>
                <td>{register.totals.garnishmentsTotal.toFixed(2)}</td>
                <td>{register.totals.netPay.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </>
      )}
    </section>
  );
}
